// Horarios del profesional: la semana, los días bloqueados y los días
// especiales, por sede.
//
// Lavada de cara oct-2026 con el diseño del asistente de primeros pasos
// (MarcaUI / Marca.css): la misma fila de día, interruptores y fichas.
// Además:
//  - "Guardar" aparece cuando hay cambios sin guardar (antes había un botón
//    arriba que quedaba lejos y otro flotante en el celular);
//  - se valida cada día (cierre antes de apertura, corte fuera del horario);
//  - el corte se guarda con las horas que se ven: tildar "Descanso" sin tocar
//    las horas dejaba breakStart vacío y el servidor guardaba el día sin corte.
import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService, Location } from '../services/api';
import { Palmtree, Plane, Home, Thermometer, MapPin, CalendarDays, Ban, Clock, CalendarPlus, Trash2, Pencil, Plus, X, AlertTriangle, CheckCircle, ListChecks } from 'lucide-react';
import ResumenHorarios from './ResumenHorarios';
import HistorialHorarios from './HistorialHorarios';
import {
  PortadaMarca, FilaDeDia, DiaEditable, errorDelDia, GrupoDeFichas,
  OpcionConInterruptor, SelectorDeHora, listaDeHoras
} from './MarcaUI';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface User {
  id: number;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type: 'salud' | 'belleza';
  especialidad: string;
}

interface WorkingDay {
  day: string;
  dayName: string;
  isWorking: boolean;
  startTime: string;
  endTime: string;
  breakStart?: string;
  breakEnd?: string;
  hasBreak: boolean;
}

interface ScheduleConfig {
  workingDays: WorkingDay[];
  appointmentDuration: number;
  bufferTime: number;
  maxAdvanceBooking: number;
  minAdvanceBookingHours: number;
  allowWeekendBooking: boolean;
  automaticConfirmation: boolean;
  timeZone: string;
  maxOverridesPerSlot: number;
  onlySpecialDays: boolean;
}

interface ScheduleException {
  id?: number;
  exception_date: string;
  exception_type: string;
  is_working: boolean;
  reason: string;
  custom_start_time?: string;
  custom_end_time?: string;
  break_start?: string;
  break_end?: string;
}

const DAY_SHORT: Record<string, string> = {
  sunday: 'Dom', monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié',
  thursday: 'Jue', friday: 'Vie', saturday: 'Sáb'
};

// Cada 5 minutos, de 06:00 a 23:55, como siempre (hay horarios guardados así)
const HORAS_DEL_DIA = listaDeHoras();

// La semana se muestra de lunes a domingo (el servidor la manda desde el domingo)
const ORDEN_SEMANA = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Un día del horario con la forma de la fila compartida (MarcaUI). Sin horas
// de corte guardadas se muestran 13:00 a 14:00, y son las que se guardan.
const aEditable = (d: WorkingDay): DiaEditable => ({
  nombre: d.dayName,
  atiende: d.isWorking,
  desde: d.startTime,
  hasta: d.endTime,
  corte: d.hasBreak,
  corteDesde: d.breakStart || '13:00',
  corteHasta: d.breakEnd || '14:00',
});

const EXCEPTION_TYPES = [
  { value: 'holiday', label: 'Feriado', icon: <Palmtree className="w-5 h-5" />, color: 'text-orange-600 bg-orange-50' },
  { value: 'vacation', label: 'Vacaciones', icon: <Plane className="w-5 h-5" />, color: 'text-blue-600 bg-blue-50' },
  { value: 'personal', label: 'Personal', icon: <Home className="w-5 h-5" />, color: 'text-purple-600 bg-purple-50' },
  { value: 'sick', label: 'Enfermedad', icon: <Thermometer className="w-5 h-5" />, color: 'text-red-600 bg-red-50' },
  { value: 'other', label: 'Otro', icon: <MapPin className="w-5 h-5" />, color: 'text-gray-600 bg-gray-50' },
];

const ProfessionalScheduleConfig: React.FC = () => {
  const navigate = useNavigate();
  // Solo en /assistant/horarios/:professionalId (la secretaria)
  const { professionalId: idDeLaRuta } = useParams<{ professionalId?: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  // Arranca en el Resumen: todo lo que decide cuándo hay turnos, a la vista
  // sin tener que acordarse de nada (oct-2026). Editar queda a un toque.
  const [activeTab, setActiveTab] = useState<'resumen' | 'schedule' | 'exceptions' | 'special_days'>('resumen');

  // ✅ Multi-sede
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Excepciones
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [loadingExceptions, setLoadingExceptions] = useState(false);
  const [showAddException, setShowAddException] = useState(false);
  const [newException, setNewException] = useState<ScheduleException>({
    exception_date: '',
    exception_type: 'holiday',
    is_working: false,
    reason: '',
  });
  const [savingException, setSavingException] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; label: string } | null>(null);

  // Días Especiales
  const [showAddSpecialDay, setShowAddSpecialDay] = useState(false);
  const [savingSpecialDay, setSavingSpecialDay] = useState(false);
  const [newSpecialDay, setNewSpecialDay] = useState({
    date: '',
    startTime: '09:00',
    endTime: '18:00',
    breakStart: '',
    breakEnd: '',
    reason: '',
  });

  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({
    workingDays: [],
    appointmentDuration: 60,
    bufferTime: 15,
    maxAdvanceBooking: 180,
    minAdvanceBookingHours: 0,
    allowWeekendBooking: false,
    automaticConfirmation: false,
    timeZone: 'America/Argentina/Buenos_Aires',
    maxOverridesPerSlot: 0,
    onlySpecialDays: false
  });

  // Lo último guardado (o cargado), para saber si hay cambios sin guardar
  const [configGuardada, setConfigGuardada] = useState<string>('');
  // Desde el primer intento de guardar, cada día muestra lo que tiene mal
  const [revisarDias, setRevisarDias] = useState(false);
  const hayCambios = configGuardada !== '' && JSON.stringify(scheduleConfig) !== configGuardada;

  const timeSlots = HORAS_DEL_DIA;

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');
    if (!userData || loginStatus !== 'true') { navigate('/login'); return; }
    try {
      const parsedUser = JSON.parse(userData);
      // La secretaria entra por /assistant/horarios/:professionalId (oct-2026):
      // la pantalla trabaja con esa profesional, como el calendario. El
      // servidor igual controla el vínculo y anota quién cambia qué.
      if (idDeLaRuta) {
        cargarProfesionalDeLaSecretaria(Number(idDeLaRuta));
        return;
      }
      if (!parsedUser.es_profesional) { navigate('/'); return; }
      setUser(parsedUser);
      // Cargar sedes y luego el horario
      loadLocationsAndSchedule(parsedUser.id);
    } catch { navigate('/login'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, idDeLaRuta]);

  // Modo secretaria: la profesional sale de su lista de vinculadas
  const cargarProfesionalDeLaSecretaria = async (id: number) => {
    try {
      const r = await fetch(`${API_URL}/api/assistants/my-professionals`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = r.ok ? await r.json() : null;
      const p = (data?.professionals || []).find((x: any) => Number(x.professional_id) === id);
      if (!p) {
        setError('No tenés acceso a los horarios de esta profesional.');
        setLoading(false);
        return;
      }
      setUser({
        id,
        nombre: p.nombre,
        apellido: p.apellido,
        es_profesional: true,
        professional_type: p.professional_type === 'belleza' ? 'belleza' : 'salud',
        especialidad: p.especialidad
      });
      loadLocationsAndSchedule(id);
    } catch {
      setError('No se pudieron cargar los horarios. Probá de nuevo.');
      setLoading(false);
    }
  };

  const loadLocationsAndSchedule = async (professionalId: number) => {
    setLoadingLocations(true);
    try {
      const locResponse = await apiService.getLocations(professionalId);
      const locs = locResponse.locations || [];
      setLocations(locs);

      // Auto-seleccionar la sede default (o la primera)
      const defaultLoc = locs.find((l: Location) => l.is_default) || locs[0];
      const locId = defaultLoc ? defaultLoc.id : null;
      setSelectedLocationId(locId);

      // Cargar horarios y excepciones con la sede seleccionada
      loadScheduleConfig(professionalId, locId);
      loadExceptions(professionalId, locId);
    } catch (err) {
      console.error('Error cargando sedes:', err);
      // Si falla, cargar sin filtro de sede (backward compatible)
      loadScheduleConfig(professionalId, null);
      loadExceptions(professionalId, null);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Cuando cambia la sede seleccionada, recargar. Con cambios sin guardar se
  // pregunta: al recargar se perdían sin aviso.
  const handleLocationChange = (locId: number | null) => {
    if (!user || locId === selectedLocationId) return;
    if (hayCambios && !window.confirm('Tenés cambios sin guardar en esta sede. Si cambiás de sede se pierden. ¿Seguís?')) return;
    setRevisarDias(false);
    setSelectedLocationId(locId);
    loadScheduleConfig(user.id, locId);
    loadExceptions(user.id, locId);
  };

  const getToken = () => {
    // Primero buscar el token directo (fuente más confiable)
    const directToken = localStorage.getItem('token');
    if (directToken) return directToken;
    // Fallback: buscar dentro del objeto user
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.token;
    }
    return null;
  };

  // Si el token expiro, cerrar sesion y redirigir al login
  const handleUnauthorized = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('token');
    window.dispatchEvent(new CustomEvent('userLogout'));
    navigate('/login');
  };

  const loadScheduleConfig = async (professionalId: number, locationId?: number | null) => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const locQuery = locationId ? `?locationId=${locationId}` : '';
      const response = await fetch(`${API_URL}/api/schedule/professional/${professionalId}/config${locQuery}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success && data.scheduleConfig) {
        setScheduleConfig(data.scheduleConfig);
        setConfigGuardada(JSON.stringify(data.scheduleConfig));
      }
    } catch (err: any) {
      setError(`Error al cargar configuracion: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadExceptions = async (professionalId: number, locationId?: number | null) => {
    setLoadingExceptions(true);
    try {
      const token = getToken();
      const locQuery = locationId ? `?locationId=${locationId}` : '';
      const response = await fetch(
        `${API_URL}/api/schedule/professional/${professionalId}/exceptions${locQuery}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.exceptions) {
          setExceptions(data.exceptions.map((e: any) => ({
            ...e,
            exception_date: typeof e.exception_date === 'string'
              ? e.exception_date.split('T')[0]
              : new Date(e.exception_date).toISOString().split('T')[0]
          })));
        }
      }
    } catch (err) {
      console.error('Error cargando excepciones:', err);
    } finally {
      setLoadingExceptions(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const workingDaysCount = scheduleConfig.workingDays.filter(d => d.isWorking).length;
      if (workingDaysCount === 0 && !scheduleConfig.onlySpecialDays) {
        setError('Prendé al menos un día de atención.');
        setSaving(false);
        return;
      }
      setRevisarDias(true);
      const conProblema = scheduleConfig.workingDays.find(d => errorDelDia(aEditable(d)));
      if (conProblema) {
        setError(`Revisá el ${conProblema.dayName.toLowerCase()}: ${errorDelDia(aEditable(conProblema))}`);
        setSaving(false);
        return;
      }
      // El corte se guarda con las horas que se ven en pantalla
      const aGuardar: ScheduleConfig = {
        ...scheduleConfig,
        workingDays: scheduleConfig.workingDays.map(d => d.hasBreak
          ? { ...d, breakStart: d.breakStart || '13:00', breakEnd: d.breakEnd || '14:00' }
          : d),
      };
      const token = getToken();
      const response = await fetch(`${API_URL}/api/schedule/professional/${user.id}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...aGuardar, locationId: selectedLocationId })
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await response.json();
      if (response.ok && data.success) {
        setScheduleConfig(aGuardar);
        setConfigGuardada(JSON.stringify(aGuardar));
        setRevisarDias(false);
        setSuccess('Horarios guardados');
        setTimeout(() => setSuccess(null), 4000);
      } else {
        throw new Error(data.error || 'Error al guardar');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveException = async () => {
    // Mensajes específicos: un solo texto cubría tres causas distintas (sesión
    // caída, fecha vacía, motivo vacío) y hacía imposible diagnosticar por qué
    // no guardaba cuando un usuario reportaba "pongo todo y me salta el alerta".
    if (!user?.id) {
      setError('Se perdió tu sesión. Cerrá sesión y volvé a entrar para bloquear el día.');
      return;
    }
    if (!newException.exception_date) {
      setError('Elegí la fecha del día que querés bloquear.');
      return;
    }
    if (!newException.reason?.trim()) {
      setError('Escribí un motivo (por ejemplo: Vacaciones).');
      return;
    }
    setSavingException(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/schedule/professional/${user.id}/exception`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          exceptionDate: newException.exception_date,
          exceptionType: newException.exception_type,
          isWorking: newException.is_working,
          reason: newException.reason,
          customStartTime: newException.is_working ? newException.custom_start_time : null,
          customEndTime: newException.is_working ? newException.custom_end_time : null,
          locationId: selectedLocationId,
        })
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('Dia bloqueado correctamente');
        setShowAddException(false);
        setNewException({ exception_date: '', exception_type: 'holiday', is_working: false, reason: '' });
        loadExceptions(user.id, selectedLocationId);
        setTimeout(() => setSuccess(null), 4000);
      } else {
        throw new Error(data.error || 'Error al guardar excepcion');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingException(false);
    }
  };

  const confirmDeleteException = (exceptionId: number, label: string) => {
    setDeleteConfirm({ id: exceptionId, label });
  };

  const handleDeleteException = async () => {
    if (!user?.id || !deleteConfirm) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/schedule/professional/${user.id}/exception/${deleteConfirm.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (response.ok) {
        setSuccess('Desbloqueado correctamente');
        loadExceptions(user.id, selectedLocationId);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Error al eliminar');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Guardar día especial
  const handleSaveSpecialDay = async () => {
    if (!user?.id || !newSpecialDay.date) {
      setError('Seleccioná una fecha');
      return;
    }
    if (newSpecialDay.startTime >= newSpecialDay.endTime) {
      setError('La hora de cierre tiene que ser después de la de apertura.');
      return;
    }
    if (newSpecialDay.breakStart) {
      if (!newSpecialDay.breakEnd || newSpecialDay.breakEnd <= newSpecialDay.breakStart) {
        setError('El corte tiene que terminar después de empezar.');
        return;
      }
      if (newSpecialDay.breakStart <= newSpecialDay.startTime || newSpecialDay.breakEnd >= newSpecialDay.endTime) {
        setError('El corte tiene que quedar dentro del horario.');
        return;
      }
    }

    // Validacion: si la fecha del dia especial cae fuera del rango de
    // anticipacion configurado, ofrecer al usuario extenderlo automaticamente.
    // Esto evita el bug donde el sistema silenciosamente no muestra el dia
    // a los pacientes (caso real reportado por Dra. Sappa en mayo 2026).
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(newSpecialDay.date + 'T00:00:00');
    const daysDiff = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const currentMax = scheduleConfig.maxAdvanceBooking || 30;

    if (daysDiff > currentMax) {
      const levels = [7, 14, 30, 60, 90, 180, 365];
      const suggested = levels.find((n) => n >= daysDiff) || 365;
      const ok = window.confirm(
        `⚠️ El día que querés activar (${newSpecialDay.date}) está a ${daysDiff} días, pero tu rango de reserva online actual es de ${currentMax} días.\n\n` +
        `Si guardás así, tus pacientes NO van a poder agendar este día.\n\n` +
        `¿Querés que extendamos el rango a ${suggested} días automáticamente para que sí lo puedan agendar?\n\n` +
        `OK = extender a ${suggested} días y guardar el día especial\n` +
        `Cancelar = volver atrás y elegir otra fecha`
      );
      if (!ok) {
        return;
      }
      // Extender max_advance_booking al nivel sugerido.
      //
      // CON la sede. Sin `locationId` el backend guardaba en la sede default
      // la configuración de la sede que se estaba mirando, horario semanal y
      // "solo días especiales" incluidos: a Adolfo Gallo le cerró Flebogen
      // entera mientras cargaba días especiales en Freschi (28-sep-2026).
      //
      // Y se mira la respuesta. Antes un error pasaba de largo, el día especial
      // se guardaba igual fuera del rango y los pacientes no lo veían, que es
      // justo lo que este cartel viene a evitar.
      try {
        const token = getToken();
        const response = await fetch(`${API_URL}/api/schedule/professional/${user.id}/config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            ...scheduleConfig,
            maxAdvanceBooking: suggested,
            locationId: selectedLocationId,
          }),
        });
        if (response.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        // Ese guardado lleva toda la configuración en pantalla: queda guardada
        setScheduleConfig({ ...scheduleConfig, maxAdvanceBooking: suggested });
        setConfigGuardada(JSON.stringify({ ...scheduleConfig, maxAdvanceBooking: suggested }));
      } catch (e) {
        setError('No se pudo extender el rango de reserva. Probá guardar de nuevo.');
        return;
      }
    }

    setSavingSpecialDay(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/schedule/professional/${user.id}/exception`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          exceptionDate: newSpecialDay.date,
          exceptionType: 'custom_hours',
          isWorking: true,
          reason: newSpecialDay.reason || 'Día especial',
          customStartTime: newSpecialDay.startTime,
          customEndTime: newSpecialDay.endTime,
          customBreakStart: newSpecialDay.breakStart || null,
          customBreakEnd: newSpecialDay.breakEnd || null,
          locationId: selectedLocationId,
        })
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('Día especial guardado correctamente');
        setShowAddSpecialDay(false);
        setNewSpecialDay({ date: '', startTime: '09:00', endTime: '18:00', breakStart: '', breakEnd: '', reason: '' });
        loadExceptions(user.id, selectedLocationId);
        setTimeout(() => setSuccess(null), 4000);
      } else {
        throw new Error(data.error || 'Error al guardar día especial');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingSpecialDay(false);
    }
  };

  // "Solo días especiales" se guarda al tocarlo (como antes), con la sede
  const toggleSoloEspeciales = (newVal: boolean) => {
    setScheduleConfig(prev => ({ ...prev, onlySpecialDays: newVal }));
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    if (user?.id) {
      fetch(`${API_URL}/api/schedule/professional/${user.id}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...scheduleConfig, onlySpecialDays: newVal, locationId: selectedLocationId })
      }).then(r => r.json()).then(data => {
        if (data.success) {
          // Ese guardado lleva toda la configuración en pantalla
          setConfigGuardada(JSON.stringify({ ...scheduleConfig, onlySpecialDays: newVal }));
          setSuccess(newVal ? 'Modo "Solo días especiales" activado' : 'Modo "Solo días especiales" desactivado');
        } else {
          setError(data.error || 'No se pudo guardar el cambio');
        }
        setTimeout(() => setSuccess(null), 3000);
      }).catch(() => setError('No se pudo guardar el cambio. Revisá tu conexión.'));
    }
  };

  // Cambio en una fila de día (MarcaUI) → el formato del servidor. Al prender
  // el corte se guardan las horas que se ven (13:00 a 14:00 si no había).
  const cambiarDia = (index: number, c: Partial<DiaEditable>) => {
    const d = scheduleConfig.workingDays[index];
    const upd: Partial<WorkingDay> = {};
    if (c.atiende !== undefined) upd.isWorking = c.atiende;
    if (c.desde !== undefined) upd.startTime = c.desde;
    if (c.hasta !== undefined) upd.endTime = c.hasta;
    if (c.corte !== undefined) {
      upd.hasBreak = c.corte;
      if (c.corte) {
        upd.breakStart = d.breakStart || '13:00';
        upd.breakEnd = d.breakEnd || '14:00';
      }
    }
    if (c.corteDesde !== undefined) upd.breakStart = c.corteDesde;
    if (c.corteHasta !== undefined) upd.breakEnd = c.corteHasta;
    updateWorkingDay(index, upd);
  };

  const descartarCambios = () => {
    if (!configGuardada) return;
    setScheduleConfig(JSON.parse(configGuardada));
    setRevisarDias(false);
    setError(null);
  };

  const updateWorkingDay = (dayIndex: number, updates: Partial<WorkingDay>) => {
    const updatedDays = [...scheduleConfig.workingDays];
    updatedDays[dayIndex] = { ...updatedDays[dayIndex], ...updates };
    setScheduleConfig({ ...scheduleConfig, workingDays: updatedDays });
  };

  const copyScheduleToAll = (sourceDay: WorkingDay) => {
    const updatedDays = scheduleConfig.workingDays.map(day => ({
      ...day,
      startTime: sourceDay.startTime,
      endTime: sourceDay.endTime,
      hasBreak: sourceDay.hasBreak,
      breakStart: sourceDay.breakStart,
      breakEnd: sourceDay.breakEnd
    }));
    setScheduleConfig({ ...scheduleConfig, workingDays: updatedDays });
    setSuccess('Horario copiado a todos los días');
    setTimeout(() => setSuccess(null), 2000);
  };

  const calculateWeeklyHours = () => {
    return scheduleConfig.workingDays
      .filter(day => day.isWorking)
      .reduce((total, day) => {
        const start = new Date(`2000-01-01 ${day.startTime}`);
        const end = new Date(`2000-01-01 ${day.endTime}`);
        const breakTime = day.hasBreak && day.breakStart && day.breakEnd
          ? new Date(`2000-01-01 ${day.breakEnd}`).getTime() - new Date(`2000-01-01 ${day.breakStart}`).getTime()
          : 0;
        return total + ((end.getTime() - start.getTime() - breakTime) / (1000 * 60 * 60));
      }, 0).toFixed(1);
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner size={48} label="Cargando horarios..." />
      </div>
    );
  }

  // Secretaria sin acceso a esa profesional (o falló la lista): nada que mostrar
  if (!user && error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 max-w-sm w-full text-left">
          <p className="text-sm text-gray-800">{error}</p>
          <button type="button" onClick={() => navigate(-1)} className="mt-4 text-sm font-semibold text-blue-600 hover:underline">Volver</button>
        </div>
      </div>
    );
  }

  const tipo: 'salud' | 'belleza' = user?.professional_type === 'belleza' ? 'belleza' : 'salud';
  const quienes = tipo === 'salud' ? 'tus pacientes' : 'tus clientes';
  const diasActivos = scheduleConfig.workingDays.filter(d => d.isWorking).length;
  const horasSemana = Number(calculateWeeklyHours());
  const bloqueosDia = exceptions
    .filter(e => !e.is_working && e.exception_type !== 'blocked_hours')
    .sort((a, b) => a.exception_date.localeCompare(b.exception_date));
  const bloqueosHorario = exceptions
    .filter(e => e.exception_type === 'blocked_hours')
    .sort((a, b) => a.exception_date.localeCompare(b.exception_date));
  const especiales = exceptions
    .filter(e => e.is_working && e.exception_type === 'custom_hours')
    .sort((a, b) => a.exception_date.localeCompare(b.exception_date));
  const ordenDias = scheduleConfig.workingDays
    .map((_, i) => i)
    .sort((a, b) => ORDEN_SEMANA.indexOf(scheduleConfig.workingDays[a].day) - ORDEN_SEMANA.indexOf(scheduleConfig.workingDays[b].day));
  const hoy = new Date().toISOString().split('T')[0];

  const pestanas: { clave: 'resumen' | 'schedule' | 'exceptions' | 'special_days'; texto: string; icono: React.ReactNode; contador?: number }[] = [
    { clave: 'resumen', texto: 'Resumen', icono: <ListChecks className="w-4 h-4" /> },
    { clave: 'schedule', texto: 'Semana', icono: <CalendarDays className="w-4 h-4" /> },
    { clave: 'exceptions', texto: 'Bloqueos', icono: <Ban className="w-4 h-4" />, contador: bloqueosDia.length + bloqueosHorario.length },
    { clave: 'special_days', texto: 'Especiales', icono: <CalendarPlus className="w-4 h-4" />, contador: especiales.length },
  ];

  // Fecha en una ficha: el número grande y el mes abajo
  const fichaFecha = (fecha: string, tono: string) => {
    const [y, m, d] = fecha.split('-').map(Number);
    const mes = new Date(y, m - 1, d).toLocaleDateString('es-AR', { month: 'short' }).replace('.', '');
    return (
      <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center leading-none shrink-0 ${tono}`}>
        <span className="text-lg font-bold">{d}</span>
        <span className="text-[10px] font-semibold uppercase mt-0.5">{mes}</span>
      </div>
    );
  };

  const tarjeta = 'bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-5';
  const campo = 'marca-campo w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-base sm:text-sm text-gray-800';
  const etiqueta = 'block text-sm font-semibold text-gray-800 mb-2';

  return (
    <div className={`marca marca--${tipo} min-h-screen bg-slate-50 text-left`}>
      <PortadaMarca
        titulo={idDeLaRuta && user ? `Horarios de ${user.nombre} ${user.apellido}` : 'Horarios'}
        bajada={idDeLaRuta
          ? 'Lo que cambies queda registrado con tu nombre.'
          : `Cuándo te pueden reservar ${quienes}.`}
        onVolver={() => navigate(-1)}
      >
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full bg-white/20 px-3 py-1">
            {scheduleConfig.onlySpecialDays ? 'Solo días especiales' : `${diasActivos} ${diasActivos === 1 ? 'día' : 'días'} por semana`}
          </span>
          {!scheduleConfig.onlySpecialDays && horasSemana > 0 && (
            <span className="rounded-full bg-white/20 px-3 py-1">
              {Number.isInteger(horasSemana) ? horasSemana : horasSemana.toFixed(1).replace('.', ',')} h de atención
            </span>
          )}
        </div>
      </PortadaMarca>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-6 relative pb-40 space-y-4">
        {/* Sede (solo con más de una). Desplegable y no botones: Gallo tiene
            5 sedes y varios nombres pasan las 30 letras; en el celular cada
            botón ocupaba un renglón entero (pedido de Esteban, oct-2026). */}
        {locations.length > 1 && activeTab !== 'resumen' && (
          <div className={`${tarjeta} animate-entrada-abajo`}>
            <label htmlFor="horarios-sede" className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              <MapPin className="w-4 h-4 marca-acento" /> Sede
            </label>
            <select
              id="horarios-sede"
              value={selectedLocationId ?? ''}
              onChange={(e) => handleLocationChange(e.target.value ? Number(e.target.value) : null)}
              className={campo}
            >
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}{loc.city ? ` — ${loc.city}` : ''}{loc.is_default ? ' (principal)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Pestañas */}
        <div
          className={`flex rounded-2xl bg-white border border-gray-200 p-1 gap-1 shadow-sm animate-entrada-abajo ${locations.length > 1 ? '' : 'mt-0'}`}
          style={{ animationDelay: '60ms' }}
          role="tablist"
        >
          {pestanas.map((p) => (
            <button
              key={p.clave}
              type="button"
              role="tab"
              aria-selected={activeTab === p.clave}
              onClick={() => setActiveTab(p.clave)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 rounded-xl py-2.5 px-1 sm:px-2 text-[13px] sm:text-sm font-semibold transition-colors ${
                activeTab === p.clave ? 'marca-tarjeta border marca-acento' : 'text-gray-500 hover:text-gray-700 border border-transparent'
              }`}
            >
              {/* Con 4 solapas, en el celular solo va el texto */}
              <span className="hidden sm:inline-flex">{p.icono}</span>
              <span className="truncate">{p.texto}</span>
              {!!p.contador && (
                <span className="hidden sm:inline rounded-full bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5">{p.contador}</span>
              )}
            </button>
          ))}
        </div>

        {/* ========== RESUMEN (solo lectura) ========== */}
        {activeTab === 'resumen' && user?.id && (
          <div className="animate-entrada-abajo space-y-4" style={{ animationDelay: '120ms' }}>
            <ResumenHorarios professionalId={user.id} />
            <HistorialHorarios professionalId={user.id} />
          </div>
        )}

        {/* ========== SEMANA ========== */}
        {activeTab === 'schedule' && (
          <>
            <section className={`${tarjeta} animate-entrada-abajo`} style={{ animationDelay: '120ms' }}>
              <h2 className="text-base font-bold text-gray-900">Días de atención</h2>
              <p className="text-sm text-gray-500 mt-0.5 mb-4">Prendé los días que atendés y elegí el horario de cada uno.</p>
              {scheduleConfig.onlySpecialDays && (
                <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Tenés prendido <strong>"Solo días especiales"</strong>: esta semana no da turnos hasta que lo apagues (en Especiales).</span>
                </div>
              )}
              <div className="space-y-2.5">
                {ordenDias.map((i) => {
                  const d = scheduleConfig.workingDays[i];
                  const editable = aEditable(d);
                  return (
                    <FilaDeDia
                      key={d.day}
                      dia={editable}
                      horas={timeSlots}
                      onCambio={(c) => cambiarDia(i, c)}
                      problema={revisarDias ? errorDelDia(editable) : null}
                      onCopiarATodos={() => copyScheduleToAll(d)}
                    />
                  );
                })}
              </div>
            </section>

            <section className={`${tarjeta} space-y-6 animate-entrada-abajo`} style={{ animationDelay: '180ms' }}>
              <div>
                <h2 className="text-base font-bold text-gray-900">Reservas online</h2>
                <p className="text-sm text-gray-500 mt-0.5">Cómo reservan {quienes} desde tu link.</p>
              </div>

              <div>
                <p className={etiqueta}>Hasta cuánto tiempo adelante</p>
                <GrupoDeFichas
                  etiqueta="Anticipación máxima"
                  opciones={[[7, '1 semana'], [14, '2 semanas'], [30, '1 mes'], [60, '2 meses'], [90, '3 meses'], [180, '6 meses'], [365, '1 año']]}
                  valor={scheduleConfig.maxAdvanceBooking}
                  onCambio={(v) => setScheduleConfig({ ...scheduleConfig, maxAdvanceBooking: v })}
                />
              </div>

              {/* Anticipación mínima — pedido de Adolfo Gallo: evitar turnos
                  sobre la hora. Solo afecta al paciente reservando desde el
                  link; vos y tu secretaria siguen cargando turnos sin límite. */}
              <div>
                <p className={etiqueta}>Con cuánta anticipación como mínimo</p>
                <GrupoDeFichas
                  etiqueta="Anticipación mínima"
                  opciones={[[0, 'Hasta último momento'], [2, '2 h antes'], [6, '6 h antes'], [12, '12 h antes'], [24, '1 día antes'], [48, '2 días antes'], [72, '3 días antes']]}
                  valor={scheduleConfig.minAdvanceBookingHours ?? 0}
                  onCambio={(v) => setScheduleConfig({ ...scheduleConfig, minAdvanceBookingHours: v })}
                />
                <p className="mt-2 text-xs leading-snug text-gray-500">
                  {(scheduleConfig.minAdvanceBookingHours ?? 0) === 0
                    ? `${quienes.charAt(0).toUpperCase()}${quienes.slice(1)} pueden reservar en cualquier horario libre, incluso para hoy.`
                    : <>{quienes.charAt(0).toUpperCase()}{quienes.slice(1)} no van a poder reservar con menos anticipación que eso.{' '}
                       <strong>Vos y tu secretaria no tienen este límite</strong>: desde el panel se puede cargar un turno para hoy mismo.</>}
                </p>
              </div>

              <div className="border-t border-gray-100 pt-5">
                <OpcionConInterruptor
                  titulo="Pausa entre turnos"
                  detalle="Tiempo libre después de cada turno."
                  activo={scheduleConfig.bufferTime > 0}
                  onCambio={(v) => setScheduleConfig({ ...scheduleConfig, bufferTime: v ? 15 : 0 })}
                >
                  <GrupoDeFichas
                    chicas
                    etiqueta="Duración de la pausa"
                    opciones={[[5, '5 min'], [10, '10 min'], [15, '15 min'], [30, '30 min']]}
                    valor={scheduleConfig.bufferTime}
                    onCambio={(v) => setScheduleConfig({ ...scheduleConfig, bufferTime: v })}
                  />
                </OpcionConInterruptor>
              </div>

              <div className="border-t border-gray-100 pt-5">
                <OpcionConInterruptor
                  titulo="Sobreturnos"
                  detalle="Permite dar más de un turno en el mismo horario."
                  activo={scheduleConfig.maxOverridesPerSlot > 0}
                  onCambio={(v) => setScheduleConfig({ ...scheduleConfig, maxOverridesPerSlot: v ? 1 : 0 })}
                >
                  <GrupoDeFichas
                    chicas
                    etiqueta="Sobreturnos por horario"
                    opciones={[[1, '+1 extra'], [2, '+2 extras'], [3, '+3 extras'], [5, '+5 extras']]}
                    valor={scheduleConfig.maxOverridesPerSlot}
                    onCambio={(v) => setScheduleConfig({ ...scheduleConfig, maxOverridesPerSlot: v })}
                  />
                </OpcionConInterruptor>
              </div>
            </section>
          </>
        )}

        {/* ========== BLOQUEOS ========== */}
        {activeTab === 'exceptions' && (
          <>
            <section className={`${tarjeta} animate-entrada-abajo`} style={{ animationDelay: '120ms' }}>
              <h2 className="text-base font-bold text-gray-900">Días bloqueados</h2>
              <p className="text-sm text-gray-500 mt-0.5 mb-4">
                Feriados, vacaciones o días personales: ese día no se puede reservar.
                {' '}Los bloqueos de horario que se cargan desde la agenda están en{' '}
                <button type="button" onClick={() => setActiveTab('resumen')} className="font-semibold marca-acento hover:underline">Resumen</button>.
              </p>

              {!showAddException ? (
                <button
                  type="button"
                  onClick={() => setShowAddException(true)}
                  className="marca-acento w-full rounded-2xl border-2 border-dashed px-4 py-3 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-50"
                  style={{ borderColor: 'var(--borde)' }}
                >
                  <Plus className="w-4 h-4" /> Bloquear un día
                </button>
              ) : (
                <div className="rounded-2xl border marca-tarjeta p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Bloquear un día</h3>
                    <button type="button" onClick={() => setShowAddException(false)} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Cerrar">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div>
                    <label htmlFor="bloqueo-fecha" className={etiqueta}>Fecha</label>
                    <input
                      id="bloqueo-fecha"
                      type="date"
                      value={newException.exception_date}
                      onChange={(e) => setNewException({ ...newException, exception_date: e.target.value })}
                      min={hoy}
                      className={campo}
                    />
                  </div>
                  <div>
                    <p className={etiqueta}>Motivo</p>
                    <GrupoDeFichas
                      chicas
                      etiqueta="Tipo de bloqueo"
                      opciones={EXCEPTION_TYPES.map(t => [t.value, t.label] as [string, string])}
                      valor={newException.exception_type}
                      onCambio={(v) => setNewException({ ...newException, exception_type: v })}
                    />
                    <input
                      type="text"
                      value={newException.reason}
                      onChange={(e) => setNewException({ ...newException, reason: e.target.value })}
                      placeholder="Ej: Feriado de Carnaval"
                      aria-label="Detalle del motivo"
                      className={`${campo} mt-3`}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddException(false)}
                      className="px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveException}
                      disabled={savingException}
                      className="marca-boton flex-1 rounded-xl py-2.5 text-sm font-semibold"
                    >
                      {savingException ? 'Guardando...' : 'Bloquear día'}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-4 space-y-2.5">
                {loadingExceptions ? (
                  <p className="text-sm text-gray-500 py-4 text-center">Cargando...</p>
                ) : bloqueosDia.length === 0 ? (
                  <div className="text-center py-6">
                    <CalendarDays className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">No tenés días bloqueados.</p>
                    <p className="text-xs text-gray-400 mt-0.5">Te pueden reservar todos tus días de atención.</p>
                  </div>
                ) : (
                  bloqueosDia.map((exc) => {
                    const typeInfo = EXCEPTION_TYPES.find(t => t.value === exc.exception_type) || EXCEPTION_TYPES[4];
                    const isPast = new Date(exc.exception_date + 'T23:59:59') < new Date();
                    return (
                      <div key={exc.id ?? exc.exception_date} className={`flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 ${isPast ? 'opacity-50' : ''}`}>
                        {fichaFecha(exc.exception_date, typeInfo.color)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 capitalize">{formatDate(exc.exception_date)}</p>
                          <p className="text-xs text-gray-500 truncate">{exc.reason} · {typeInfo.label}</p>
                        </div>
                        {!isPast && (
                          <button
                            type="button"
                            onClick={() => confirmDeleteException(exc.id!, `Desbloquear ${formatDate(exc.exception_date)} (${typeInfo.label})`)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                            title="Desbloquear día"
                            aria-label={`Desbloquear ${formatDate(exc.exception_date)}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            {/* Horarios bloqueados desde el calendario */}
            {!loadingExceptions && bloqueosHorario.length > 0 && (
              <section className={`${tarjeta} animate-entrada-abajo`} style={{ animationDelay: '180ms' }}>
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" /> Horarios bloqueados
                </h2>
                <p className="text-sm text-gray-500 mt-0.5 mb-4">
                  Días que cortaste antes de hora desde el calendario. Podés desbloquearlos acá.
                </p>
                <div className="space-y-2.5">
                  {bloqueosHorario.map((exc, index) => {
                    const isPast = new Date(exc.exception_date + 'T23:59:59') < new Date();
                    const startTime = exc.custom_start_time ? exc.custom_start_time.substring(0, 5) : '—';
                    const endTime = exc.custom_end_time ? exc.custom_end_time.substring(0, 5) : '—';
                    return (
                      <div key={`blocked-${index}`} className={`flex items-center gap-3 rounded-2xl border border-red-100 bg-white p-3 ${isPast ? 'opacity-50' : ''}`}>
                        {fichaFecha(exc.exception_date, 'text-red-600 bg-red-50')}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 capitalize">{formatDate(exc.exception_date)}</p>
                          <p className="text-xs text-gray-500">Atiende {startTime} a {endTime} · bloqueado desde {endTime}</p>
                          {exc.reason && <p className="text-xs text-gray-400 truncate">{exc.reason}</p>}
                        </div>
                        {!isPast && (
                          <button
                            type="button"
                            onClick={() => confirmDeleteException(exc.id!, `Desbloquear horarios del ${formatDate(exc.exception_date)}`)}
                            className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl border border-red-200"
                          >
                            Desbloquear
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {/* ========== DÍAS ESPECIALES ========== */}
        {activeTab === 'special_days' && (
          <section className={`${tarjeta} animate-entrada-abajo space-y-4`} style={{ animationDelay: '120ms' }}>
            <div>
              <h2 className="text-base font-bold text-gray-900">Días especiales</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Abrí un día que normalmente no atendés, con su propio horario.
                {scheduleConfig.workingDays.filter(d => !d.isWorking).length > 0 && (
                  <> Hoy no atendés: <strong>{scheduleConfig.workingDays.filter(d => !d.isWorking).map(d => DAY_SHORT[d.day]).join(', ')}</strong>.</>
                )}
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 p-4">
              <OpcionConInterruptor
                titulo="Solo días especiales"
                detalle="Apaga la semana: solo se reserva en los días que agregues acá."
                activo={scheduleConfig.onlySpecialDays}
                onCambio={toggleSoloEspeciales}
              >
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-2.5">
                  {quienes.charAt(0).toUpperCase()}{quienes.slice(1)} solo ven disponibles los días especiales de abajo.
                </p>
              </OpcionConInterruptor>
            </div>

            {!showAddSpecialDay ? (
              <button
                type="button"
                onClick={() => setShowAddSpecialDay(true)}
                className="marca-acento w-full rounded-2xl border-2 border-dashed px-4 py-3 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-50"
                style={{ borderColor: 'var(--borde)' }}
              >
                <Plus className="w-4 h-4" /> Agregar día especial
              </button>
            ) : (
              <div className="rounded-2xl border marca-tarjeta p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Día especial</h3>
                  <button type="button" onClick={() => setShowAddSpecialDay(false)} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Cerrar">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div>
                  <label htmlFor="especial-fecha" className={etiqueta}>Fecha</label>
                  <input
                    id="especial-fecha"
                    type="date"
                    value={newSpecialDay.date}
                    onChange={(e) => setNewSpecialDay({ ...newSpecialDay, date: e.target.value })}
                    min={hoy}
                    className={campo}
                  />
                  {/* Aviso si la fecha cae fuera del rango de anticipación: sin
                      él, el día no les aparecía a los pacientes (Dra. Sappa,
                      mayo 2026). */}
                  {(() => {
                    if (!newSpecialDay.date) return null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const targetDate = new Date(newSpecialDay.date + 'T00:00:00');
                    const daysDiff = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    const max = scheduleConfig.maxAdvanceBooking || 30;
                    if (daysDiff <= max) return null;
                    const levels = [7, 14, 30, 60, 90, 180, 365];
                    const suggested = levels.find((n) => n >= daysDiff) || 365;
                    return (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-800 leading-relaxed">
                          <p className="font-semibold mb-0.5">Fecha fuera del rango de reserva online</p>
                          <p>
                            Esta fecha cae a <strong>{daysDiff} días</strong> y hoy te pueden reservar hasta <strong>{max} días</strong> adelante.
                            Al guardar te vamos a ofrecer ampliarlo a <strong>{suggested} días</strong>.
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <p className={etiqueta}>Horario</p>
                  <div className="flex items-center gap-2">
                    <SelectorDeHora etiqueta="Día especial: desde" valor={newSpecialDay.startTime} horas={timeSlots} onCambio={(h) => setNewSpecialDay({ ...newSpecialDay, startTime: h })} />
                    <span className="text-sm text-gray-500">a</span>
                    <SelectorDeHora etiqueta="Día especial: hasta" valor={newSpecialDay.endTime} horas={timeSlots} onCambio={(h) => setNewSpecialDay({ ...newSpecialDay, endTime: h })} />
                  </div>
                  {newSpecialDay.breakStart ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-gray-600">Corte</span>
                      <SelectorDeHora etiqueta="Día especial: corte desde" valor={newSpecialDay.breakStart} horas={timeSlots} onCambio={(h) => setNewSpecialDay({ ...newSpecialDay, breakStart: h })} />
                      <span className="text-sm text-gray-500">a</span>
                      <SelectorDeHora etiqueta="Día especial: corte hasta" valor={newSpecialDay.breakEnd || '14:00'} horas={timeSlots} onCambio={(h) => setNewSpecialDay({ ...newSpecialDay, breakEnd: h })} />
                      <button
                        type="button"
                        onClick={() => setNewSpecialDay({ ...newSpecialDay, breakStart: '', breakEnd: '' })}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        aria-label="Sacar el corte"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setNewSpecialDay({ ...newSpecialDay, breakStart: '13:00', breakEnd: '14:00' })}
                      className="marca-acento mt-2 inline-flex items-center gap-1 text-xs font-semibold"
                    >
                      <Plus className="w-3.5 h-3.5" /> Agregar un corte (ej: al mediodía)
                    </button>
                  )}
                </div>

                <div>
                  <label htmlFor="especial-motivo" className={etiqueta}>Descripción (opcional)</label>
                  <input
                    id="especial-motivo"
                    type="text"
                    value={newSpecialDay.reason}
                    onChange={(e) => setNewSpecialDay({ ...newSpecialDay, reason: e.target.value })}
                    placeholder="Ej: Atención en Resistencia, guardia especial..."
                    className={campo}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddSpecialDay(false)}
                    className="px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveSpecialDay}
                    disabled={savingSpecialDay || !newSpecialDay.date}
                    className="marca-boton flex-1 rounded-xl py-2.5 text-sm font-semibold"
                  >
                    {savingSpecialDay ? 'Guardando...' : 'Guardar día especial'}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2.5">
              {loadingExceptions ? (
                <p className="text-sm text-gray-500 py-4 text-center">Cargando...</p>
              ) : especiales.length === 0 ? (
                <div className="text-center py-6">
                  <CalendarPlus className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No hay días especiales.</p>
                  <p className="text-xs text-gray-400 mt-0.5">Sirven para atender un día fuera de tu semana habitual.</p>
                </div>
              ) : (
                especiales.map((exc) => {
                  const isPast = new Date(exc.exception_date + 'T23:59:59') < new Date();
                  return (
                    <div key={exc.id ?? exc.exception_date} className={`flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 ${isPast ? 'opacity-50' : ''}`}>
                      {fichaFecha(exc.exception_date, 'marca-tarjeta border marca-acento')}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 capitalize">{formatDate(exc.exception_date)}</p>
                        <p className="text-xs text-gray-500">
                          {exc.custom_start_time?.substring(0, 5)} a {exc.custom_end_time?.substring(0, 5)}
                          {exc.break_start && exc.break_end && ` · corte ${exc.break_start.substring(0, 5)} a ${exc.break_end.substring(0, 5)}`}
                        </p>
                        {exc.reason && <p className="text-xs text-gray-400 truncate">{exc.reason}</p>}
                      </div>
                      {!isPast && (
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              setNewSpecialDay({
                                date: exc.exception_date.split('T')[0],
                                startTime: exc.custom_start_time?.substring(0, 5) || '09:00',
                                endTime: exc.custom_end_time?.substring(0, 5) || '18:00',
                                breakStart: exc.break_start?.substring(0, 5) || '',
                                breakEnd: exc.break_end?.substring(0, 5) || '',
                                reason: exc.reason || '',
                              });
                              setShowAddSpecialDay(true);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                            title="Editar día especial"
                            aria-label={`Editar ${formatDate(exc.exception_date)}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => confirmDeleteException(exc.id!, `Sacar el día especial del ${formatDate(exc.exception_date)}`)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                            title="Sacar día especial"
                            aria-label={`Sacar ${formatDate(exc.exception_date)}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        )}
      </div>

      {/* Avisos: arriba, debajo del encabezado del sitio */}
      {(error || success) && (
        <div className="fixed inset-x-0 top-20 z-[60] px-4 pointer-events-none">
          <div
            className={`max-w-md mx-auto pointer-events-auto flex items-start gap-2 rounded-2xl border px-4 py-3 text-sm shadow-lg animate-slide-up ${
              error ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-gray-200 text-gray-800'
            }`}
            role={error ? 'alert' : 'status'}
          >
            {error ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />}
            <span className="flex-1">{error || success}</span>
            <button type="button" onClick={() => { setError(null); setSuccess(null); }} className="opacity-60 hover:opacity-100" aria-label="Cerrar aviso">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Guardar: aparece cuando hay cambios en la semana. En el celular,
          arriba de la barra de abajo (fab-sobre-barra). */}
      {activeTab === 'schedule' && (hayCambios || saving) && (
        <div className="fab-sobre-barra fixed inset-x-0 bottom-4 sm:bottom-6 z-40 px-4 pointer-events-none">
          <div className="max-w-3xl mx-auto pointer-events-auto flex items-center gap-2 rounded-2xl bg-white border border-gray-200 shadow-xl px-3 py-2.5 animate-slide-up">
            <span className="flex-1 text-sm font-medium text-gray-700 pl-1">Tenés cambios sin guardar</span>
            <button
              type="button"
              onClick={descartarCambios}
              disabled={saving}
              className="px-3 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="marca-boton px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
            >
              {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {/* Confirmación para desbloquear o sacar */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[70] p-4 animate-backdrop-in">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 animate-modal-in">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">¿Seguro?</h3>
                <p className="text-xs text-gray-500">Lo podés volver a cargar cuando quieras</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-5">{deleteConfirm.label}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteException}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
              >
                Sí, sacar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalScheduleConfig;
